/**
 * UTM utilities — capture from URL on landing, persist in cookies, attach to leads.
 *
 * Behavior (atomic refresh):
 * - On landing, if the URL has any UTM, ALL previous UTM cookies are cleared
 *   and the new ones are stored. This avoids mixing campaigns.
 * - If the URL has no UTM and also has no `unidad` / `unit`, previous UTM
 *   cookies are cleared so plain navigations do not keep stale attribution.
 * - If the URL only carries `unidad` / `unit`, existing UTMs are preserved so
 *   detail/contact flows can still use the attribution captured on landing.
 * - Cookies live `UTM_COOKIE_DAYS` days, refreshed each time new UTMs arrive.
 * - `getUtms()` reads them back as a flat object (only present keys).
 */

export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];

export const UTM_COOKIE_DAYS = 30;
export const UNIT_KEYS = ['unidad', 'unit'];

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const setCookie = (name, value, days) => {
  if (!isBrowser()) return;
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;
  // SameSite=Lax keeps cookies on top-level navigations (typical landing flow)
  document.cookie = `${name}=${encodeURIComponent(value)}; ${expires}; path=/; SameSite=Lax`;
};

const deleteCookie = (name) => {
  if (!isBrowser()) return;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
};

const readCookie = (name) => {
  if (!isBrowser()) return null;
  const match = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  if (!match) return null;
  const raw = match.slice(name.length + 1);
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

/**
 * Read UTMs from a search string (e.g. window.location.search).
 * Returns an object containing only present, non-empty UTM keys.
 */
export const parseUtmsFromSearch = (search) => {
  if (!search) return {};
  const params = new URLSearchParams(search);
  const out = {};
  for (const key of UTM_KEYS) {
    const v = params.get(key);
    if (v && v.trim()) out[key] = v.trim();
  }
  return out;
};

export const hasUnitParam = (search) => {
  if (!search) return false;
  const params = new URLSearchParams(search);

  return UNIT_KEYS.some((key) => {
    const value = params.get(key);
    return Boolean(value && value.trim());
  });
};

const clearStoredUtms = () => {
  UTM_KEYS.forEach(deleteCookie);
};

/**
 * If the current URL carries any UTM, clear previous UTM cookies and store
 * the incoming set. If the URL has neither UTM params nor unidad/unit, any
 * previously stored UTM cookies are cleared.
 */
export const captureUtmsFromUrl = (search = isBrowser() ? window.location.search : '') => {
  const incoming = parseUtmsFromSearch(search);
  if (Object.keys(incoming).length === 0) {
    if (!hasUnitParam(search)) {
      clearStoredUtms();
    }

    return null;
  }

  // Atomic replace: clear all previous UTM cookies first
  clearStoredUtms();

  // Persist incoming UTMs
  Object.entries(incoming).forEach(([k, v]) => setCookie(k, v, UTM_COOKIE_DAYS));

  return incoming;
};

/**
 * Read all currently-stored UTMs as a flat object. Empty if the current URL
 * does not carry utm_* params nor unidad/unit.
 */
export const getUtms = (search = isBrowser() ? window.location.search : '') => {
  const incoming = parseUtmsFromSearch(search);

  if (Object.keys(incoming).length > 0) {
    return incoming;
  }

  if (!hasUnitParam(search)) {
    return {};
  }

  const out = {};
  for (const key of UTM_KEYS) {
    const v = readCookie(key);
    if (v) out[key] = v;
  }
  return out;
};
